import type { Editor } from "@tiptap/core";
import type { UseEventBusReturn } from "@vueuse/core";
import type { Point } from "ls/features/disko/interop/dotnetExports/editor";
import type { ElementNode } from "ls/features/disko/rich-text/streaming-markdown";
import type { RequestOptions } from "../common/server";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useEventBus } from "@vueuse/core";
import { useToast } from "ls/components/simple-toast/useToast";
import { getInsertLocation, insertAxvgMini } from "ls/features/disko/interop/dotnetExports/bridge";
import { parseMarkdownLine } from "ls/features/disko/rich-text/streaming-markdown";
import { joinURL } from "ufo";
import { reactive, ref } from "vue";
import { cloudApiUrl } from "../urls";
import { server } from "./server";

export interface AiResponse {
  content: string;
  isDocument: boolean;
  inputTokenCount: number;
  outputTokenCount: number;
  cachedTokenCount: number;
}

export interface Screen {
  screen: string;
  description: string;
}

export interface ScreenList {
  screens: Screen[];
  size: {
    width: number;
    height: number;
  };
}

async function generateDiagram(prompt: string, { signal }: RequestOptions = {}): Promise<AiResponse> {
  const response = await server.get("ai/GenerateDiagram", {
    signal,
    searchParams: {
      prompt,
    },
  });
  return await response.json() as AiResponse;
}

const SPACING = 100;
export async function generateAiContent(prompt: string, { signal }: RequestOptions = {}): Promise<AiResponse> {
  const response = await server.get("ai/GenerateAiContent", {
    signal,
    searchParams: {
      prompt,
    },
  });
  const aiResponse = await response.json() as AiResponse;
  const hasScreenList = aiResponse.content.includes("<ScreenList>");
  if (hasScreenList) {
    const screenList = aiResponse.content.match(/<ScreenList>(.*?)<\/ScreenList>/s);
    if (screenList) {
      const screenListJson = JSON.parse(screenList[1]) as ScreenList;
      const insertLocation = JSON.parse(getInsertLocation(screenListJson.size.width, screenListJson.size.height, screenListJson.screens.length)) as Point;
      for (let i = 0; i < screenListJson.screens.length; i++) {
        const screenIndex = i;
        const screen = screenListJson.screens[i];

        console.log(`Generating diagram for screen ${i}, idx:${screenIndex} ${screen.screen}\n${screen.description}\nScreen Size: ${screenListJson.size.width}x${screenListJson.size.height}`);
        const diagramResponsePromise = generateDiagram(`Screen Name: ${screen.screen}\nScreen Description: ${screen.description}\nScreen Size: ${screenListJson.size.width}x${screenListJson.size.height}\nList of screens: S\`\`\`${screenListJson}\`\`\``, { signal });
        diagramResponsePromise.then(diagramResponse => {
          console.log(diagramResponse.content);
          const locationX = insertLocation.x + SPACING + screenIndex * (SPACING + screenListJson.size.width);
          console.log(`Location X: ${locationX}`);
          insertAxvgMini(diagramResponse.content, locationX, insertLocation.y);
          // console.log(`Inserted diagram ${diagramResponse.inputTokenCount} input tokens, ${diagramResponse.outputTokenCount} output tokens, ${diagramResponse.cachedTokenCount} cached tokens`);
        });
      }
    }
  }
  return aiResponse;
}

export async function generateAiDocument(prompt: string, { signal }: RequestOptions = {}): Promise<AiResponse> {
  const response = await server.get("ai/GenerateAiDocument", {
    signal,
    searchParams: {
      prompt,
    },
  });
  return await response.json() as AiResponse;
}

export async function generateAiDocumentPrompt(prompt: string, chatId: string, { signal }: RequestOptions = {}): Promise<AiResponse> {
  const response = await server.get("ai/GenerateAiDocumentPrompt", {
    signal,
    searchParams: {
      prompt,
      chatId,
    },
  });
  return await response.json() as AiResponse;
}

export interface MessageContent {
  index: number;
  text: string;
  markdown: ElementNode;
}

// this is to track state data for the incoming message.
export interface IncomingMessageState {
  buf: string[];
  endsWithNewline: boolean;
}
export interface Message {
  id?: string;
  role: "user" | "assistant";
  content: MessageContent[];
  incomingMessageContent?: MessageContent;
  incomingMessageState?: IncomingMessageState;
  inputTokenCount?: number;
  outputTokenCount?: number;
  cachedTokenCount?: number;
  isIncoming?: boolean;
  hidden?: boolean;
}

export type ConversationContext = "chat";
// interface ActionTags {
//   [key: string]: (actionInput: string) => void;
// }
export interface Conversation {
  chatId: string;
  editor?: Editor;
  context?: ConversationContext;
  messages: Message[];
  onAction: UseEventBusReturn<void, void>;
  hasStarted: boolean;
  placeholderId?: string;
  snippet?: string;
  startConversation: (context: ConversationContext) => Promise<void>;
  finishConversation: () => void;
  submitConversation: (replyToMessageId?: string) => Promise<void>;
  triggerAction: () => void;
  pushMessage: (message: Message) => void;
  clearMessages: () => void;
}

interface ConversationState {
  conversation: Conversation;
}

export interface Options {
  initialMessage?: string;
}

const { toast } = useToast();
export class AiSseClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async streamConversation(
    endpoint: "startConversation" | "submitPrompt",
    conversation: Conversation,
    payload: any,
    callbacks: {
      onError?: (message: string) => void;
      onComplete?: () => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const url = joinURL(this.baseUrl, "ai", endpoint);

    conversation.messages.push({
      role: "assistant",
      content: [],
    });

    await fetchEventSource(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Authorization: `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(payload),
      signal,
      keepalive: true,
      async onopen(response) {
        if (response.ok) return;

        if (response.status >= 400 && response.status < 500) {
          const error = await response.text();
          throw new Error(`Client error (${response.status}): ${error}`);
        }

        console.error(`Server error: ${response.status}`);
      },
      onmessage(event) {
        const data = event.data ? JSON.parse(event.data) : undefined;

        switch (event.event) {
          case "assistantMessageStarted": {
            const role = "assistant";
            const lastMessage = conversation.messages[conversation.messages.length - 1];
            lastMessage.id = data.messageId;
            lastMessage.role = role;
            lastMessage.isIncoming = true;
          }
            break;
          case "assistantMessageFragment": {
            const lastMessage = conversation.messages[conversation.messages.length - 1];
            updateMessageWithFragment(lastMessage, data.text);
          }
            break;
          case "assistantMessageCompleted": {
            const lastMessage = conversation.messages[conversation.messages.length - 1];
            finalizeIncomingMessage(lastMessage);
            lastMessage.isIncoming = false;
            lastMessage.incomingMessageContent = undefined;
          }
            break;
          case "error":
            callbacks.onError?.(data.message);
            break;
          case "complete":
            callbacks.onComplete?.();
            break;
          case "aiCreditLimitReached":
            toast({
              title: "AI credit limit reached",
            });
            break;
        }
      },

      onclose() {
        callbacks.onComplete?.();
      },

      onerror(error) {
        console.error("SSE error:", error);
        callbacks.onError?.(error.message);

        // Throw to stop retrying on client errors
        if (error.message?.includes("Client error")) {
          throw error;
        }
      },
    });
  }

  async clearMessages(conversation: Conversation) {
    const url = joinURL(this.baseUrl, "ai", "clearMessages");
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ chatId: conversation.chatId }),
    });
  }
}

let currentIndex = 0; // use for uniquely tagging content lines.
function getBlankMessageContent(): MessageContent {
  return {
    index: currentIndex++,
    text: "",
    markdown: parseMarkdownLine(""),
  };
}

export function sanitizeEditorHtml(html: string, placeholderId: string, placeholderText: string = "[[ GENERATED TEXT HERE ]]"): string {
  // Replace <a> tags with data-type="aiNode" and matching data-id
  const aiNodeRegex = new RegExp(
    `<a\\s+[^>]*data-type="aiNode"[^>]*data-id="${placeholderId}"[^>]*>.*?</a>`,
    "gi",
  );

  let sanitized = html.replace(aiNodeRegex, placeholderText);

  // Remove all of the other aiNodes
  const aiNodesRegex = /<a\s[^>]*data-type="aiNode"[^>]*>.*?<\/a>/gi;
  sanitized = sanitized.replace(aiNodesRegex, "");

  // Strip out all div tags (opening and closing), keeping the content
  sanitized = sanitized.replace(/<\/?div[^>]*>/gi, "");

  return sanitized;
}

function updateMessageWithFragment(msg: Message, chunk: string) {
  const state = (msg.incomingMessageState ||= {
    buf: [],
    endsWithNewline: false,
  });

  let cur = msg.incomingMessageContent;
  if (!cur) {
    cur = msg.incomingMessageContent = getBlankMessageContent();
  }

  // Check if we have a double newline split across chunks
  if (state.endsWithNewline && chunk.startsWith("\n")) {
    if (state.buf.length > 0) {
      cur.text = state.buf.join("\n");
      cur.markdown = parseMarkdownLine(cur.text);
      msg.content.push(cur);
      state.buf = [];
      cur = msg.incomingMessageContent = getBlankMessageContent();
    }
    chunk = chunk.slice(1);
  }

  // Prepend the last incomplete line (if any) to the new chunk
  if (state.buf.length > 0 && !state.endsWithNewline) {
    chunk = state.buf.pop() + chunk;
  }

  state.endsWithNewline = chunk.endsWith("\n");

  // Split and process
  const lines = chunk.split("\n");
  const lastLine = lines.pop() || "";

  for (const line of lines) {
    if (line === "") {
      if (state.buf.length > 0) {
        cur.text = state.buf.join("\n");
        cur.markdown = parseMarkdownLine(cur.text);
        msg.content.push(cur);
        state.buf = [];
        cur = msg.incomingMessageContent = getBlankMessageContent();
      }
    } else {
      state.buf.push(line);
    }
  }

  // Push the last line (complete or incomplete) to buf
  if (lastLine || !state.endsWithNewline) {
    state.buf.push(lastLine);
  }

  // Update current block
  cur.text = state.buf.join("\n");
  cur.markdown = parseMarkdownLine(cur.text);
}

/** Call when the stream ends to flush any remaining content */
function finalizeIncomingMessage(msg: Message) {
  const st = msg.incomingMessageState;
  const cur = msg.incomingMessageContent;

  // If we have buffered content that hasn't been pushed yet, it means
  // the stream ended without a final double newline
  if (st?.buf.length && cur) {
    // The current block already has its text/markdown updated,
    // but we need to push it to msg.content
    msg.content.push(cur);

    // Clear the state
    st.buf = [];
    msg.incomingMessageContent = undefined;
  }
}

const activeChat = ref<Conversation | undefined>(undefined);
export function clearActiveChat() {
  activeChat.value = undefined;
}
export function useActiveChat() {
  return activeChat;
}

// const conversationIdToChatId = new Map<string, string>();
// export function getChatId(conversationId: string) {
//   const chatId = conversationIdToChatId.get(conversationId);
//   if (!chatId) {
//     const chatId = newGuid();
//     conversationIdToChatId.set(conversationId, chatId);
//     return chatId;
//   }
//   return chatId;
// }

// export function getInitialMessages(options?: Options): Message[] {
//   if (options?.initialMessage) {
//     return [{
//       role: "template-message",
//       content: [{
//         index: 0,
//         text: options.initialMessage,
//         markdown: parseMarkdownLine(options.initialMessage),
//       }],
//     }];
//   }
//   return [];
// }

export function getMarkdown(messageContent: MessageContent[]): string {
  return messageContent.map(c => c.text).join("\n\n");
}

interface ConversationState {
  conversation: Conversation;
  currentEventSource?: EventSource;
  abortController?: AbortController;
}

const conversations = reactive(new Map<string, ConversationState>());

export function useAiChat(chatId: string) {
  const cachedConversationState = conversations.get(chatId);
  if (cachedConversationState) {
    return cachedConversationState.conversation;
  }

  const conversationState: ConversationState = reactive({
    conversation: {
      messages: [],
      chatId,
      placeholderId: undefined,
      hasActionTags: false,
      onAction: useEventBus("ai:action"),
      hasStarted: false,
      startConversation,
      finishConversation,
      submitConversation,
      triggerAction,
      pushMessage,
      clearMessages,
    },
  });

  conversations.set(chatId, conversationState);
  const conversation = conversationState.conversation;

  function pushMessage(message: Message) {
    conversation.messages.push(message);
  }

  async function clearMessages() {
    conversation.messages = [];
    const sseClient = new AiSseClient(cloudApiUrl);
    await sseClient.clearMessages(conversation);
  }

  async function startConversation(context: ConversationContext) {
    conversation.context = context;
    conversation.hasStarted = true;

    // Cancel any existing request
    conversationState.abortController?.abort();
    conversationState.abortController = new AbortController();

    // create a dictionary of the messages
    const initialMessages = conversation.messages.map(message => ({
      role: message.role,
      content: message.content.map(c => c.text).join("\n"),
    }));

    const sseClient = new AiSseClient(cloudApiUrl);
    await sseClient.streamConversation(
      "startConversation",
      conversation,
      {
        context,
        chatId: conversation.chatId,
        initialMessages,
        metadata: {
          snippet: conversation.snippet,
          template: sanitizeEditorHtml(conversation.editor?.getHTML() ?? "", conversation.placeholderId ?? ""),
        },
      },
      {}, // callbacks
      conversationState.abortController.signal,
    );
  }

  function finishConversation() {
    // Abort any ongoing SSE streams
    conversationState.abortController?.abort();
    conversationState.abortController = undefined;

    conversations.delete(conversation.chatId);
    if (activeChat.value === conversation) {
      activeChat.value = undefined;
    }
  }

  function triggerAction() {
    conversation.onAction.emit();
  }

  async function submitConversation(replyToMessageId?: string) {
    if (!conversation) {
      throw new Error("No conversation started");
    }

    const lastMessage = conversation.messages.at(-1);
    if (!lastMessage) {
      return;
    }

    // const apiUrl = joinURL(cloudApiUrl, "ai", "submitPrompt");

    // try {
    //   // Cancel any existing request
    //   conversationState.abortController?.abort();
    //   conversationState.abortController = new AbortController();

    //   const response = await fetch(apiUrl, {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //       Authorization: `Bearer ${getAuthToken()}`,
    //     },
    //     body: JSON.stringify({
    //       chatId: conversation.chatId,
    //       prompt: lastMessage.content.map(c => c.text).join("\n"),
    //       replyToMessageId,
    //     }),
    //     signal: conversationState.abortController.signal,
    //   });

    //   if (!response.ok) {
    //     throw new Error(`HTTP error! status: ${response.status}`);
    //   }

    //   await handleSseStream(response, conversationState);
    // } catch (err) {
    //   if (err instanceof Error && err.name !== "AbortError") {
    //     console.error("Submit conversation failed: ", err);
    //   }
    // }

    // Cancel any existing request
    conversationState.abortController?.abort();
    conversationState.abortController = new AbortController();

    const sseClient = new AiSseClient(cloudApiUrl);
    await sseClient.streamConversation(
      "submitPrompt",
      conversation,
      {
        chatId: conversation.chatId,
        prompt: lastMessage.content.map(c => c.text).join("\n"),
        replyToMessageId,
      },
      {}, // callbacks
      conversationState.abortController.signal,
    );
  }

  return conversation;
}
